import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, Form, Message,
} from 'semantic-ui-react';
import get from 'lodash/get';

import { useParams } from 'react-router';
import FormFields from '../../../../components/FormFields';
import Header from '../../../../components/Header';
import styles from './EditTeam.module.scss';
import useFocusFirstInput from '../../../../hooks/useFocusFirstInput';
import useForm from '../../../../hooks/useForm';
import { CLUB_TEAMS_MEMBERS_ROUTE } from '../../../../constants/routes';
import { EditTeamProps, ERROR_MESSAGE_TRANSLATIONS } from './EditTeamProps';
import { createTeamSchema, validationSchema } from './schema';

const EditTeam = ({
  editTeam,
  clubId,
  fetchTeamId,
  teamsByHash,
  loading,
  error,
  errorMessage,
  fetchTeam,
  fetchClubById,
}: EditTeamProps) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [translate] = useTranslation();
  const teamId = get(useParams(), 'teamId') as string;
  const team = teamsByHash[teamId];

  const {
    values,
    errors,
    handleChange,
    validate,
    setValues,
  } = useForm(validationSchema);

  useFocusFirstInput();

  useEffect(() => {
    if (!teamId && clubId) {
      fetchTeamId(clubId);
    }
  }, [clubId, fetchTeamId, teamId]);

  useEffect(() => {
    fetchClubById();
  }, [clubId, fetchClubById]);

  useEffect(() => {
    if (team) {
      setValues((currentValues: any) => ({
        ...currentValues,
        teamName: team.name || null,
      }));
    }
  }, [team, setValues]);

  useEffect(() => {
    if (teamId && clubId) {
      fetchTeam(+teamId);
    }
  }, [clubId, fetchTeam, teamId]);

  const handleSubmit = () => {
    setIsSubmitted(true);
    if (validate() && clubId) {
      editTeam(
        {
          ...team,
          name: values.teamName,
        },
      );
    }
  };

  const renderButton = () => (
    <Button
      primary
      disabled={loading}
      type="submit"
      content={translate('SAVE')}
      form="create-form"
    />
  );

  const renderErrorMessage = () => (
    <Message
      error={error}
      header={translate('ERROR')}
      content={
        (errorMessage && !!Object.keys(ERROR_MESSAGE_TRANSLATIONS).includes(errorMessage))
          ? translate(
            ERROR_MESSAGE_TRANSLATIONS[errorMessage as keyof typeof ERROR_MESSAGE_TRANSLATIONS],
          ) : translate('GENERAL_ERROR')}
    />
  );

  return (
    <div className={styles.wrapper}>
      <Header
        backLabel="TO_MY_TEAMS"
        backLinkTo={CLUB_TEAMS_MEMBERS_ROUTE.replace(':teamId', teamId)}
        renderButton={renderButton}
        title={translate('EDIT_TEAM')}
        border
      />

      <Form error={error} onSubmit={handleSubmit} className={styles.form} id="create-form">
        {isSubmitted && errors && !loading && renderErrorMessage()}
        <FormFields
          errors={errors}
          handleChange={handleChange}
          schema={createTeamSchema}
          values={values}
        />
      </Form>
    </div>
  );
};

export default EditTeam;
